import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import IYClogo from "../images/IYClogo.png";
import { Search, X } from "lucide-react";
import { ScheduleMarquee } from "../pages/StateSchedulePage";

/* ================= SEARCH DATA ================= */
const SEARCH_PAGES = [
  { title: "Home", path: "/" },
  { title: "Events", path: "/state-schedule" },

  { title: "How to Become a Member", path: "/how-to-become-member" },
  { title: "Membership Model", path: "/membership-model" },
  { title: "Membership Count", path: "/membership-count" },
  { title: "State Mapping", path: "/state-mapping" },

  { title: "Voter List", path: "/voter-list" },
  { title: "On Hold List", path: "/on-hold-list" },
  { title: "Reject List", path: "/reject-list" },
  { title: "Voter Search", path: "/voter-search" },
  { title: "View Nomination", path: "/view-nomination" },

  { title: "Performers List", path: "/performers-list" },
  { title: "Election Result", path: "/get-result" },

  { title: "RO Details", path: "/ro-details" },
  { title: "RO Schedule", path: "/ro-schedule" },
  { title: "Reserved Districts", path: "/reserved-districts" },
];

export default function Header() {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedStateCode, setSelectedStateCode] = useState(
    () => localStorage.getItem("selectedStateCode") || ""
  );

  /* ===== SEARCH STATE ===== */
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const scrutinyRef = useRef(null);
  const membershipRef = useRef(null);
  const electionsRef = useRef(null);
  const contactRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  /* ===== LOAD SCHEDULE FROM STORAGE (FOR MARQUEE) ===== */
  const schedule = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("stateSchedule")) || [];
    } catch {
      return [];
    }
  }, [location.pathname]);

  /* ===== CLEAR SEARCH ON ROUTE CHANGE ===== */
  useEffect(() => {
    setSearchQuery("");
    setSearchResults([]);
    setShowSearch(false);
    setActiveIndex(-1);
  }, [location.pathname]);

  /* ===== SEARCH LOGIC ===== */
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setActiveIndex(-1);

    if (!value.trim()) {
      setSearchResults([]);
      setShowSearch(false);
      return;
    }

    const q = value.toLowerCase();
    const filtered = SEARCH_PAGES.filter((p) =>
      p.title.toLowerCase().includes(q)
    );

    setSearchResults(filtered);
    setShowSearch(true);
  };

  /* ===== KEYBOARD NAVIGATION ===== */
  const handleSearchKeyDown = (e) => {
    if (!showSearch) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) =>
        Math.min(i + 1, searchResults.length - 1)
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    }

    if (e.key === "Enter") {
      e.preventDefault();

      if (activeIndex >= 0 && searchResults[activeIndex]) {
        navigate(searchResults[activeIndex].path);
      } else if (searchQuery.trim()) {
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      }

      setShowSearch(false);
    }
  };

  /* ===== HIGHLIGHT MATCH ===== */
  const highlightMatch = (text) => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery})`, "ig");
    return text.split(regex).map((part, i) =>
      regex.test(part) ? <mark key={i}>{part}</mark> : part
    );
  };

  /* ===== HELPERS ===== */
  const closeAllMenus = () => {
    setOpenMenu("");
    setMobileOpen(false);
  };

  /* ===== STATE SYNC ===== */
  useEffect(() => {
    const onStateChange = () => {
      setSelectedStateCode(localStorage.getItem("selectedStateCode") || "");
    };
    window.addEventListener("stateChange", onStateChange);
    onStateChange();
    return () => window.removeEventListener("stateChange", onStateChange);
  }, []);

  /* ===== OUTSIDE CLICK ===== */
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        scrutinyRef.current?.contains(e.target) ||
        membershipRef.current?.contains(e.target) ||
        electionsRef.current?.contains(e.target) ||
        contactRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpenMenu("");
      setShowSearch(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <>
      {/* ===== MARQUEE ABOVE HEADER =====
      {selectedStateCode && schedule.length > 0 && (
        <ScheduleMarquee schedule={schedule} />
      )} */}

      <header className="app-header">
        <div className="app-header-inner">

          {/* LOGO */}
          <div className="brand">
            <Link to="https://www.iyc.in">
              <img src={IYClogo} alt="Indian Youth Congress" />
            </Link>
          </div>

   {/* SEARCH */}
{selectedStateCode && (
  <>
  
 <button
  className="mobile-search-toggle"
  onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
>
  <Search size={20} />
</button>

<div className={`header-search ${mobileSearchOpen ? "mobile-open" : ""}`}>
  <Search size={16} className="search-icon" />

      <input
        type="text"
        placeholder="Search pages..."
        value={searchQuery}
        onChange={(e) => handleSearchChange(e.target.value)}
        onKeyDown={handleSearchKeyDown}
        onFocus={() => searchResults.length && setShowSearch(true)}
      />

 {searchQuery && (
    <button
      className="search-clear"
      onClick={() => {
        setSearchQuery("");
        setSearchResults([]);
        setShowSearch(false);
      }}
    >
      <X size={16} />
    </button>
  )}

      {showSearch && (
        <ul className="search-suggestions">
          {searchResults.length > 0 ? (
            searchResults.map((item, index) => (
              <li
                key={item.path}
                className={index === activeIndex ? "active" : ""}
                onMouseDown={(e) => {
                  e.preventDefault();
                  navigate(item.path);
                  setShowSearch(false);
                }}
              >
                {highlightMatch(item.title)}
              </li>
            ))
          ) : (
            <li className="search-no-results">
              No matching pages found
            </li>
          )}
        </ul>
      )}
    </div>
  </>
)}

          {/* HOME ONLY */}
          {!selectedStateCode && (
            <Link className="home-only-link" to="/">
              Home
            </Link>
          )}

          {/* HAMBURGER */}
          {selectedStateCode && (
            <button
              className="hamburger"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              ☰
            </button>
          )}

          {/* NAVIGATION */}
          {selectedStateCode && (
            <nav className={mobileOpen ? "open mobile-nav" : ""}>
              <ul className="nav-ul">

                <li><Link to="/" onClick={closeAllMenus}>Home</Link></li>
                <li><Link to="/state-schedule" onClick={closeAllMenus}>Events</Link></li>

                <li ref={membershipRef} className="dropdown-wrapper">
                  <button onClick={() => setOpenMenu(openMenu === "membership" ? "" : "membership")}>
                    Membership ▾
                  </button>
                  {openMenu === "membership" && (
                    <ul className="scrutiny-dropdown">
                      {/* <li><Link to="/how-to-become-member" onClick={closeAllMenus}>How to Become a Member</Link></li> */}
                      <li><Link to="/membership-model" onClick={closeAllMenus}>Membership Model</Link></li>
                      <li><Link to="/membership-count" onClick={closeAllMenus}>Membership Count</Link></li>
                      <li><Link to="/state-mapping" onClick={closeAllMenus}>State Mapping</Link></li>
                      <li><Link to="/reserved-districts" onClick={closeAllMenus}>Reserved Districts</Link></li>
                    </ul>
                  )}
                </li>

                <li ref={scrutinyRef} className="dropdown-wrapper">
  <button
    onClick={() =>
      setOpenMenu(openMenu === "scrutiny" ? "" : "scrutiny")
    }
  >
    Scrutiny ▾
  </button>

  {openMenu === "scrutiny" && (
    <ul className="scrutiny-dropdown">
      <li
        onClick={() => {
          sessionStorage.setItem("selectedStatus", "0");
          closeAllMenus();
          navigate("/voter-list");
        }}
      >
        <span>Voter List</span>
      </li>

      <li
        onClick={() => {
          sessionStorage.setItem("selectedStatus", "1");
          closeAllMenus();
          navigate("/on-hold-list");
        }}
      >
        <span>On Hold List</span>
      </li>

      <li
        onClick={() => {
          sessionStorage.setItem("selectedStatus", "2");
          closeAllMenus();
          navigate("/reject-list");
        }}
      >
        <span>Reject List</span>
      </li>

      <li
        onClick={() => {
          closeAllMenus();
          navigate("/voter-search");
        }}
      >
        <span>Voter Search</span>
      </li>

      <li
        onClick={() => {
          closeAllMenus();
          navigate("/view-nomination");
        }}
      >
        <span>View Nomination</span>
      </li>
    </ul>
  )}
</li>

                <li ref={electionsRef} className="dropdown-wrapper">
                  <button onClick={() => setOpenMenu(openMenu === "elections" ? "" : "elections")}>
                    Elections ▾
                  </button>
                  {openMenu === "elections" && (
                    <ul className="scrutiny-dropdown">
                      <li><Link to="/performers-list" onClick={closeAllMenus}>Performers List</Link></li>
                      <li><Link to="/get-result" onClick={closeAllMenus}>Election Result</Link></li>
                    </ul>
                  )}
                </li>

                <li ref={contactRef} className="dropdown-wrapper">
                  <button onClick={() => setOpenMenu(openMenu === "contactus" ? "" : "contactus")}>
                    Contact Us ▾
                  </button>
                  {openMenu === "contactus" && (
                    <ul className="scrutiny-dropdown">
                      <li><Link to="/ro-details" onClick={closeAllMenus}>RO Details</Link></li>
                      <li><Link to="/ro-schedule" onClick={closeAllMenus}>RO Schedule</Link></li>
                    </ul>
                  )}
                </li>

              </ul>
            </nav>
          )}

        </div>
      </header>
    </>
  );
}
